import { Bubble } from '@ant-design/x'
import type { BubbleItemType } from '@ant-design/x'
import {
  Button,
  ConfigProvider,
  Divider,
  Flex,
  Skeleton,
  theme,
  Typography
} from 'antd'
import type { GetRef } from 'antd'
import { ChevronDown } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode, UIEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { useScrollFade } from 'src/hooks'

import { ChatAttachmentList } from './ChatAttachmentList'
import {
  type ChatComposerExtras,
  type ChatComposerHandle,
  ChatComposer
} from './ChatComposer'
import { ChatMarkdown } from './ChatMarkdown'
import { CopyButton } from './CopyButton'
import { useStyles } from './styles'
import { ThinkingShimmer } from './ThinkingShimmer'
import type { ChatMessage, Conversation } from './types'
import type { ResolveAttachmentUrl } from './useAttachmentActions'

const { Text } = Typography

const SCROLL_DOWN_OFFSET = 120
const SKELETON_BODY_ROWS = 4
const DEFAULT_MODEL_NAME = 'Skalx AI'
const CHAT_FONT_SIZE_BUMP = 1

type Props = {
  conversation: Conversation
  input: string
  onChange: (value: string) => void
  onSend: () => void
  onStop: () => void
  streaming: boolean
  disabled?: boolean
  hideComposer?: boolean
  hideTitle?: boolean
  compact?: boolean
  modelName?: string
  showCopyTooltip?: boolean
  loadingHistory?: boolean
  aiIcon?: ReactNode
  renderFeedback?: (message: ChatMessage, threadId: string) => ReactNode
  renderTimeline?: (message: ChatMessage, streaming: boolean) => ReactNode
  resolveAttachmentUrl?: ResolveAttachmentUrl
  composerExtras?: ChatComposerExtras
}

const ChatConversationSkeleton = () => {
  const { styles, cx } = useStyles()

  return (
    <div className={cx(styles.conversationSkeleton, styles.enterSlow)}>
      <div className={styles.userSkeletonRow}>
        <Skeleton.Input active className={styles.userSkeletonBubble} />
      </div>
      <div className={styles.aiSkeletonBlock}>
        <Flex align="center" gap={8}>
          <Skeleton.Avatar active size={24} />
          <Skeleton.Input
            active
            size="small"
            className={styles.aiSkeletonName}
          />
        </Flex>
        <Skeleton
          active
          title={false}
          paragraph={{ rows: SKELETON_BODY_ROWS }}
        />
      </div>
    </div>
  )
}

export const ChatConversation = ({
  conversation,
  input,
  onChange,
  onSend,
  onStop,
  streaming,
  disabled,
  hideComposer,
  hideTitle,
  compact,
  modelName = DEFAULT_MODEL_NAME,
  showCopyTooltip = true,
  loadingHistory,
  aiIcon,
  renderFeedback,
  renderTimeline,
  resolveAttachmentUrl,
  composerExtras
}: Props) => {
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const chatFontSize = token.fontSize + CHAT_FONT_SIZE_BUMP
  const composerRef = useRef<ChatComposerHandle>(null)
  const bubbleListRef = useRef<GetRef<typeof Bubble.List>>(null)
  const { ref: attachScrollFade, className: scrollFadeClass } = useScrollFade()
  const { t } = useTranslation()
  const [showScrollDown, setShowScrollDown] = useState(false)

  const messages = conversation.messages
  const lastId = messages.length ? messages[messages.length - 1].id : null
  const threadId = conversation.id
  const showSkeleton = !!loadingHistory && messages.length === 0

  useEffect(() => {
    composerRef.current?.focus()
  }, [conversation.id])

  useEffect(() => {
    const frame = requestAnimationFrame(() =>
      attachScrollFade(bubbleListRef.current?.scrollBoxNativeElement ?? null)
    )

    return () => {
      cancelAnimationFrame(frame)
      attachScrollFade(null)
    }
  }, [attachScrollFade, showSkeleton])

  const items = useMemo<BubbleItemType[]>(
    () =>
      messages.map((message) => ({
        key: message.id,
        role: message.role === 'user' ? 'user' : 'ai',
        content: message
      })),
    [messages]
  )

  const roles = useMemo(() => {
    const renderAttachments = (message: ChatMessage) =>
      message.attachments?.length ? (
        <ChatAttachmentList
          attachments={message.attachments}
          resolveUrl={resolveAttachmentUrl}
        />
      ) : null

    const assistantBody = (message: ChatMessage, isStreaming: boolean) => {
      if (message.timeline && message.timeline.length > 0) {
        return renderTimeline?.(message, isStreaming) ?? null
      }
      if (message.content) return <ChatMarkdown content={message.content} />
      if (isStreaming) return <ThinkingShimmer />

      return null
    }

    return {
      user: {
        placement: 'end' as const,
        rootClassName: styles.userBubble,
        contentRender: (content: ChatMessage) => {
          const attachments = renderAttachments(content)

          if (!attachments) {
            return <span className={styles.userText}>{content.content}</span>
          }

          return (
            <Flex vertical gap="small" align="flex-end">
              {content.content ? (
                <span className={styles.userText}>{content.content}</span>
              ) : null}
              {attachments}
            </Flex>
          )
        },
        footer: (content: ChatMessage) =>
          content.content.trim() ? (
            <Flex align="center" gap={2}>
              <CopyButton
                text={content.content}
                showTooltip={showCopyTooltip}
              />
            </Flex>
          ) : null
      },
      ai: {
        placement: 'start' as const,
        variant: 'borderless' as const,
        style: { width: '100%', paddingInlineEnd: 0, paddingBlock: 0 },
        styles: {
          header: { marginBottom: 8 },
          footer: { marginTop: 8 },
          body: { width: '100%' },
          content: { width: '100%', padding: 0 }
        },
        header: (
          <Flex align="center" gap={8}>
            {aiIcon}
            <Text strong className={styles.aiHeaderName}>
              {modelName}
            </Text>
          </Flex>
        ),
        contentRender: (
          content: ChatMessage,
          info: { key?: string | number }
        ) => {
          const body = assistantBody(content, streaming && info.key === lastId)
          const attachments = renderAttachments(content)

          if (!attachments) return body

          return (
            <Flex vertical gap="small">
              {body}
              {attachments}
            </Flex>
          )
        },
        footer: (content: ChatMessage, info: { key?: string | number }) => {
          if (streaming && info.key === lastId) return null
          const hasText = !!content.content.trim()
          const feedback = renderFeedback?.(content, threadId)
          if (!hasText && !feedback) return null

          return (
            <Flex align="center" gap={2}>
              {hasText ? (
                <CopyButton
                  text={content.content}
                  showTooltip={showCopyTooltip}
                />
              ) : null}
              {hasText && feedback ? (
                <Divider
                  orientation="vertical"
                  className={styles.aiActionsDivider}
                />
              ) : null}
              {feedback}
            </Flex>
          )
        }
      }
    }
  }, [
    styles,
    streaming,
    lastId,
    threadId,
    modelName,
    showCopyTooltip,
    aiIcon,
    renderFeedback,
    renderTimeline,
    resolveAttachmentUrl
  ])

  const chatTheme = useMemo(
    () => ({
      token: {
        fontSize: chatFontSize,
        fontSizeSM: chatFontSize,
        fontSizeLG: chatFontSize,
        fontSizeXL: chatFontSize,
        fontSizeHeading1: chatFontSize,
        fontSizeHeading2: chatFontSize,
        fontSizeHeading3: chatFontSize,
        fontSizeHeading4: chatFontSize,
        fontSizeHeading5: chatFontSize
      }
    }),
    [chatFontSize]
  )

  const scrollToBottom = () =>
    bubbleListRef.current?.scrollTo({ top: 'bottom' })

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    setShowScrollDown(event.currentTarget.scrollTop < -SCROLL_DOWN_OFFSET)
  }

  return (
    <ConfigProvider theme={chatTheme}>
      <Flex vertical className={styles.conversation}>
        {hideTitle ? null : (
          <Flex
            align="center"
            justify="space-between"
            className={styles.conversationHeader}
          >
            <Text strong className={styles.conversationTitle} ellipsis>
              {conversation.title}
            </Text>
          </Flex>
        )}

        <div className={styles.bubbleListWrap}>
          {showSkeleton ? (
            <ChatConversationSkeleton />
          ) : (
            <>
              <Bubble.List
                ref={bubbleListRef}
                autoScroll
                items={items}
                role={roles}
                className={cx(
                  styles.bubbleList,
                  compact && styles.bubbleListCompact
                )}
                classNames={{
                  scroll: scrollFadeClass
                }}
                onScroll={onScroll}
              />
              {showScrollDown ? (
                <Button
                  shape="circle"
                  icon={<ChevronDown size={18} />}
                  className={styles.scrollDownButton}
                  onClick={scrollToBottom}
                  aria-label={t('workspaceHome.scrollToBottom')}
                />
              ) : null}
            </>
          )}
        </div>

        {hideComposer ? null : (
          <div className={styles.conversationComposer}>
            <ChatComposer
              ref={composerRef}
              value={input}
              onChange={onChange}
              onSend={onSend}
              onStop={onStop}
              streaming={streaming}
              disabled={disabled}
              {...composerExtras}
            />
          </div>
        )}
      </Flex>
    </ConfigProvider>
  )
}
